import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Handshake,
  Plus,
  Search,
  Building2,
  Loader2,
  Sparkles,
  Send,
  Trash2,
  ArrowLeft,
  RefreshCw,
  Save,
} from 'lucide-react';
import { Button, Badge, Modal } from '../components/ui';
import { RetroUploadModal, ScrubbedPayloadPane, RetroDraftEditor } from '../components/retros';
import { cn } from '../lib/utils';
import {
  getTenants,
  getMeetingRetros,
  getMeetingRetro,
  generateMeetingRetro,
  updateMeetingRetro,
  publishMeetingRetro,
  deleteMeetingRetro,
  RETRO_STATUS_LABELS,
  getRetroStatusVariant,
} from '../services';
import type { Tenant, MeetingRetro, RetroDraftContent } from '../data/types';

/**
 * Meeting Retros — monthly security meeting retrospectives.
 *
 * Flow: upload the Teams transcript → the backend parses and scrubs entity
 * indicators → analyst reviews the exact scrubbed payload (and can redact
 * misses) → generate the AI draft → edit → publish to the customer portal.
 * Left rail: customer list (Notebook-style). Right: retro list or workspace.
 */
export function MeetingRetros() {
  const { tenantId, retroId } = useParams<{ tenantId: string; retroId: string }>();
  const navigate = useNavigate();

  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [tenantsLoading, setTenantsLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [retros, setRetros] = useState<MeetingRetro[]>([]);
  const [retrosLoading, setRetrosLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  const [showUpload, setShowUpload] = useState(false);

  useEffect(() => {
    getTenants().then((r) => {
      if (r.success) setTenants(r.data);
      setTenantsLoading(false);
    });
  }, []);

  const loadRetros = useCallback(async (id: string) => {
    setRetrosLoading(true);
    setListError(null);
    const r = await getMeetingRetros(id);
    if (r.success) setRetros(r.data);
    else setListError(r.error || 'Failed to load retros');
    setRetrosLoading(false);
  }, []);

  useEffect(() => {
    if (tenantId && !retroId) loadRetros(tenantId);
  }, [tenantId, retroId, loadRetros]);

  const selectedTenant = useMemo(
    () => tenants.find((t) => t.tenantId === tenantId) ?? null,
    [tenants, tenantId]
  );

  const filteredTenants = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q
      ? tenants.filter(
          (t) => t.displayName.toLowerCase().includes(q) || t.tenantId.toLowerCase().includes(q)
        )
      : tenants;
  }, [tenants, search]);

  return (
    <div className="flex h-full min-h-0 gap-4">
      {/* Left rail: customer list */}
      <aside className="w-64 shrink-0 flex flex-col border border-border-default rounded-lg bg-bg-secondary overflow-hidden">
        <div className="p-3 border-b border-border-default">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-text-secondary" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Find customer…"
              className="w-full pl-8 pr-2 py-1.5 text-sm rounded-md border border-border-default bg-bg-primary text-text-primary focus:outline-none focus:ring-1 focus:ring-brand-blue"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {tenantsLoading ? (
            <div className="p-4 flex justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-text-secondary" />
            </div>
          ) : (
            filteredTenants.map((t) => (
              <button
                key={t.tenantId}
                onClick={() => navigate(`/retros/${t.tenantId}`)}
                className={cn(
                  'w-full text-left px-3 py-2.5 flex items-center gap-2 border-b border-border-default/50 hover:bg-bg-tertiary transition-colors',
                  t.tenantId === tenantId && 'bg-bg-tertiary'
                )}
              >
                <Building2 className="w-4 h-4 text-text-secondary shrink-0" />
                <span className="text-sm text-text-primary truncate">{t.displayName}</span>
              </button>
            ))
          )}
        </div>
      </aside>

      {/* Right: workspace */}
      <main className="flex-1 min-w-0 min-h-0 flex flex-col">
        {!tenantId ? (
          <EmptyState />
        ) : retroId ? (
          <RetroWorkspace
            tenantId={tenantId}
            retroId={retroId}
            onBack={() => navigate(`/retros/${tenantId}`)}
          />
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-lg font-semibold text-text-primary">
                  {selectedTenant?.displayName ?? tenantId}
                </h1>
                <p className="text-sm text-text-secondary">Monthly security meeting retros</p>
              </div>
              <Button onClick={() => setShowUpload(true)}>
                <Plus className="w-4 h-4 mr-1" /> Upload transcript
              </Button>
            </div>

            {retrosLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-text-secondary" />
              </div>
            ) : listError ? (
              <p className="text-sm text-accent-red py-8 text-center">{listError}</p>
            ) : retros.length === 0 ? (
              <div className="text-center py-16 text-text-secondary">
                <Handshake className="w-8 h-8 mx-auto mb-3 opacity-50" />
                <p className="text-sm">No retros yet — upload the meeting transcript to start.</p>
              </div>
            ) : (
              <div className="space-y-2 overflow-y-auto">
                {retros.map((r) => (
                  <button
                    key={r.retroId}
                    onClick={() => navigate(`/retros/${tenantId}/${r.retroId}`)}
                    className="w-full text-left border border-border-default rounded-lg px-4 py-3 bg-bg-secondary hover:bg-bg-tertiary transition-colors flex items-center justify-between gap-3"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-text-primary truncate">{r.title}</p>
                      <p className="text-xs text-text-secondary">
                        Meeting {r.meetingDate}
                        {r.publishedAt ? ` · published ${r.publishedAt.slice(0, 10)}` : ''}
                      </p>
                    </div>
                    <Badge variant={getRetroStatusVariant(r.status)}>
                      {RETRO_STATUS_LABELS[r.status]}
                    </Badge>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </main>

      {tenantId && selectedTenant && (
        <RetroUploadModal
          isOpen={showUpload}
          onClose={() => setShowUpload(false)}
          tenantId={tenantId}
          tenantName={selectedTenant.displayName}
          onUploaded={(retro) => {
            setShowUpload(false);
            navigate(`/retros/${tenantId}/${retro.retroId}`);
          }}
        />
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex-1 flex items-center justify-center text-center text-text-secondary">
      <div>
        <Handshake className="w-10 h-10 mx-auto mb-3 opacity-50" />
        <p className="text-sm">Select a customer to view or create meeting retros.</p>
      </div>
    </div>
  );
}

interface RetroWorkspaceProps {
  tenantId: string;
  retroId: string;
  onBack: () => void;
}

function RetroWorkspace({ tenantId, retroId, onBack }: RetroWorkspaceProps) {
  const [retro, setRetro] = useState<MeetingRetro | null>(null);
  const [draft, setDraft] = useState<RetroDraftContent | null>(null);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'generate' | 'save' | 'publish' | 'delete' | 'tokenize' | null>(
    null
  );
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const applyRetro = (r: MeetingRetro) => {
    setRetro(r);
    setDraft(r.draft ?? null);
    setDirty(false);
  };

  useEffect(() => {
    setLoading(true);
    setError(null);
    getMeetingRetro(tenantId, retroId).then((r) => {
      if (r.success) applyRetro(r.data);
      else setError(r.error || 'Failed to load retro');
      setLoading(false);
    });
  }, [tenantId, retroId]);

  const handleGenerate = async () => {
    setBusy('generate');
    setError(null);
    const r = await generateMeetingRetro(tenantId, retroId);
    if (r.success) applyRetro(r.data);
    else setError(r.error || 'Draft generation failed');
    setBusy(null);
  };

  const handleSave = async () => {
    if (!draft) return;
    setBusy('save');
    setError(null);
    const r = await updateMeetingRetro(tenantId, retroId, { draft });
    if (r.success) applyRetro(r.data);
    else setError(r.error || 'Save failed');
    setBusy(null);
  };

  const handleTokenize = async (value: string) => {
    setBusy('tokenize');
    setError(null);
    const r = await updateMeetingRetro(tenantId, retroId, { addManualEntities: [value] });
    if (r.success) applyRetro(r.data);
    else setError(r.error || 'Redaction failed');
    setBusy(null);
  };

  const handlePublish = async () => {
    setConfirmPublish(false);
    setBusy('publish');
    setError(null);
    // Persist any local edits before the publish snapshot.
    if (dirty && draft) {
      const saved = await updateMeetingRetro(tenantId, retroId, { draft });
      if (!saved.success) {
        setError(saved.error || 'Save failed');
        setBusy(null);
        return;
      }
    }
    const r = await publishMeetingRetro(tenantId, retroId);
    if (r.success) applyRetro(r.data);
    else setError(r.error || 'Publish failed');
    setBusy(null);
  };

  const handleDelete = async () => {
    setConfirmDelete(false);
    setBusy('delete');
    const r = await deleteMeetingRetro(tenantId, retroId);
    if (r.success) onBack();
    else {
      setError(r.error || 'Delete failed');
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-text-secondary" />
      </div>
    );
  }
  if (!retro) {
    return <p className="text-sm text-accent-red py-8 text-center">{error ?? 'Not found'}</p>;
  }

  const isPublished = retro.status === 'published';

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="min-w-0">
            <h1 className="text-lg font-semibold text-text-primary truncate">{retro.title}</h1>
            <p className="text-xs text-text-secondary">
              Meeting {retro.meetingDate}
              {retro.approvedBy ? ` · approved by ${retro.approvedBy}` : ''}
            </p>
          </div>
          <Badge variant={getRetroStatusVariant(retro.status)}>
            {RETRO_STATUS_LABELS[retro.status]}
          </Badge>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!isPublished && retro.status === 'draft_ready' && (
            <>
              <Button variant="secondary" size="sm" onClick={handleSave} disabled={busy !== null || !dirty}>
                <Save className="w-4 h-4 mr-1" /> {busy === 'save' ? 'Saving…' : 'Save'}
              </Button>
              <Button variant="secondary" size="sm" onClick={handleGenerate} disabled={busy !== null}>
                <RefreshCw className={cn('w-4 h-4 mr-1', busy === 'generate' && 'animate-spin')} />
                {busy === 'generate' ? 'Regenerating…' : 'Regenerate'}
              </Button>
              <Button size="sm" onClick={() => setConfirmPublish(true)} disabled={busy !== null}>
                <Send className="w-4 h-4 mr-1" /> Publish
              </Button>
            </>
          )}
          {!isPublished && retro.status === 'scrubbed' && (
            <Button size="sm" onClick={handleGenerate} disabled={busy !== null}>
              <Sparkles className={cn('w-4 h-4 mr-1', busy === 'generate' && 'animate-pulse')} />
              {busy === 'generate' ? 'Drafting (30–60s)…' : 'Generate draft'}
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(true)} disabled={busy !== null}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {error && <p className="text-sm text-accent-red mb-3">{error}</p>}
      {retro.failureReason && retro.status === 'scrubbed' && (
        <p className="text-sm text-accent-orange mb-3">
          Last generation attempt failed: {retro.failureReason}
        </p>
      )}

      {retro.status === 'scrubbed' && (
        <div className="flex-1 min-h-0 flex flex-col">
          <p className="text-sm text-text-secondary mb-3">
            Review the scrubbed payload below — this is exactly what will be sent to the drafting
            model. Nothing has been sent yet. Select any missed value and redact it, then generate.
          </p>
          <div className="flex-1 min-h-0">
            <ScrubbedPayloadPane
              scrubbedTranscript={retro.scrubbedTranscript ?? ''}
              entityMap={retro.entityMap ?? {}}
              onTokenize={handleTokenize}
              tokenizing={busy === 'tokenize'}
            />
          </div>
        </div>
      )}

      {retro.status !== 'scrubbed' && draft && (
        <div className="flex-1 min-h-0 grid grid-cols-1 xl:grid-cols-2 gap-4">
          <div className="min-h-0 overflow-y-auto pr-1">
            <RetroDraftEditor
              draft={draft}
              readOnly={isPublished}
              onChange={(d) => {
                setDraft(d);
                setDirty(true);
              }}
            />
          </div>
          <div className="min-h-0 hidden xl:block">
            <ScrubbedPayloadPane
              scrubbedTranscript={retro.scrubbedTranscript ?? ''}
              entityMap={retro.entityMap ?? {}}
              onTokenize={isPublished ? undefined : handleTokenize}
              tokenizing={busy === 'tokenize'}
            />
          </div>
        </div>
      )}

      <Modal
        isOpen={confirmPublish}
        onClose={() => setConfirmPublish(false)}
        title="Publish retro to customer portal?"
        description="The approved content (summary, topics, decisions, action items) becomes visible to the customer. Transcript and scrubbed payload stay internal."
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmPublish(false)}>
              Cancel
            </Button>
            <Button onClick={handlePublish}>
              {busy === 'publish' ? 'Publishing…' : 'Publish'}
            </Button>
          </>
        }
      />

      <Modal
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="Delete retro?"
        description={
          isPublished
            ? 'This also removes the published retro from the customer portal.'
            : 'The retro and its transcript working copy are removed.'
        }
        danger
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmDelete(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              {busy === 'delete' ? 'Deleting…' : 'Delete'}
            </Button>
          </>
        }
      />
    </div>
  );
}
